import { DeleteOutlined, EditOutlined, ExportOutlined } from '@ant-design/icons';
import { Form, Image, Input, type TableColumnType, Tag } from 'antd';
import type { ProdutoBodyVariacaoGrade } from '@/features/produto/types/ProdutoBody';
import { InputNumber } from '@/components/input/InputNumber';
import { TableActionsInline } from '@/components/table/table-actions/TableActionsInline';
import { TableActionsSaveCancel } from '@/components/table/table-actions/TableActionsSaveCancel';
import { numberToString } from '@/std/format';
import { ProdutoTabKey } from '../../edit/body/useProdutoTabItems';
import { UploadImage } from './UploadImage';

export function getColumns({
    editingKey,
    edit,
    save,
    cancel,
    remove,
    selectFoto,
}: {
    editingKey: string | null;
    edit: (grade: ProdutoBodyVariacaoGrade) => void;
    save: () => void;
    cancel: () => void;
    remove: (descricao: string) => void;
    selectFoto: (fileName: string, base64: string) => void;
}): TableColumnType<ProdutoBodyVariacaoGrade>[] {
    return [
        {
            dataIndex: 'pro_descricao',
            render: (value, record) =>
                record.pro_idpk === null ? (
                    <>
                        {value}
                        <Tag color='green' style={{ marginLeft: 10 }} variant='solid'>
                            Novo
                        </Tag>
                    </>
                ) : (
                    value
                ),
            title: 'Variação',
        },
        {
            align: 'center',
            dataIndex: 'pro_sku',
            render: (value, record) => {
                if (record.pro_descricao === editingKey) {
                    return (
                        <Form.Item name='pro_sku' style={{ margin: 0 }}>
                            <Input value={value} />
                        </Form.Item>
                    );
                }

                return value;
            },
            title: 'SKU',
        },
        {
            dataIndex: 'pro_cod_barra',
            render: (value, record) => {
                if (record.pro_descricao === editingKey) {
                    return (
                        <Form.Item name='pro_cod_barra' style={{ margin: 0 }}>
                            <Input value={value} />
                        </Form.Item>
                    );
                }

                return value;
            },
            title: 'Código de barras',
            width: '12rem',
        },
        {
            align: 'right',
            dataIndex: 'pro_preco_venda',
            render: (value, record) => {
                if (record.pro_descricao === editingKey) {
                    return (
                        <Form.Item name='pro_preco_venda' style={{ margin: 0 }}>
                            <InputNumber decimalScale={2} value={value} />
                        </Form.Item>
                    );
                }

                return numberToString(value, 'currency');
            },
            title: 'Preço',
            width: '6rem',
        },
        {
            align: 'right',
            dataIndex: 'estoque_total',
            render: (estoque: number, record) => {
                if (record.pro_controlar_estoque === 0) {
                    return 'Não controla';
                }

                if (record.pro_descricao === editingKey) {
                    return (
                        <Form.Item name='estoque_total' noStyle={true}>
                            <InputNumber decimalScale={2} />
                        </Form.Item>
                    );
                }

                return estoque;
            },
            title: 'Estoque',
            width: '7rem',
        },
        {
            align: 'center',
            dataIndex: 'foto',
            render: (value, record) => {
                if (record.pro_descricao === editingKey) {
                    return (
                        <>
                            <Form.Item hidden={true} name='produto_foto' />
                            <UploadImage onChange={selectFoto} />
                        </>
                    );
                }

                const foto = value
                    ? value
                    : record.produto_foto?.[0]?.foto_base64
                      ? `data:image/png;base64,${record.produto_foto?.[0]?.foto_base64}`
                      : undefined;

                return foto ? (
                    <Image
                        alt=''
                        preview={{ zIndex: 1051 }}
                        src={foto}
                        style={{ aspectRatio: 1, height: 32 }}
                    />
                ) : null;
            },
            responsive: ['sm'],
            title: 'Imagem',
        },
        {
            align: 'center',
            key: 'actions',
            render: (_, record) => {
                if (record.pro_descricao === editingKey) {
                    return <TableActionsSaveCancel onCancel={cancel} onSave={save} />;
                }

                const hasProduto = typeof record.pro_idpk === 'number';

                return (
                    <TableActionsInline
                        buttons={[
                            {
                                icon: <ExportOutlined />,
                                id: 'ir-para-o-produto',
                                link: {
                                    params: { action: 'editar', idpk: Number(record.pro_idpk) },
                                    search: { tab1: ProdutoTabKey.Geral },
                                    target: '_blank',
                                    to: '/a/produto/$action/{-$idpk}',
                                },

                                tooltip: 'Ir para o produto',
                                visible: hasProduto,
                            },
                            {
                                icon: <EditOutlined />,
                                id: 'editar',
                                onClick: () => edit(record),
                                tooltip: 'Editar',
                                type: 'primary',
                            },
                            {
                                confirm: 'Tem certeza que deseja excluir?',
                                danger: true,
                                icon: <DeleteOutlined />,
                                id: 'excluir',
                                onClick: () => remove(record.pro_descricao),
                                tooltip: 'Excluir',
                                type: 'primary',
                            },
                        ]}
                    />
                );
            },
            title: 'Ações',
        },
    ];
}
